// Formatierer für Größen, Zeiten und Fehler (deutsch, ohne externe Bibliotheken).
import { ApiError } from '../../lib/api';

const BYTE_UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB'] as const;

/** Bytes menschenlesbar, binäre Präfixe (1 KiB = 1024 B). */
export function formatBytes(bytes: number | null | undefined, digits = 1): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '–';
  let value = bytes;
  let unit = 0;
  while (Math.abs(value) >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  const text = unit === 0 ? String(Math.round(value)) : value.toFixed(digits).replace(/\.0+$/, '');
  return `${text.replace('.', ',')} ${BYTE_UNITS[unit]}`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '–';
  return new Intl.NumberFormat('de-DE').format(n);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Relative Zeitangabe („in 5 Minuten", „vor 2 Stunden"). */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '–';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '–';
  const diff = t - now;
  const rtf = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60_000) return rtf.format(Math.round(diff / 1000), 'second');
  if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
  if (abs < 86_400_000) return rtf.format(Math.round(diff / 3_600_000), 'hour');
  return rtf.format(Math.round(diff / 86_400_000), 'day');
}

/** ISO-Zeitpunkt → Wert für `<input type="datetime-local">` (lokale Zeit, Minutengenau). */
export function toLocalInput(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Wert aus `<input type="datetime-local">` → ISO (UTC). Leer/ungültig → null. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Fehlermeldung für die Oberfläche (API-Fehlerformat, sonst generisch). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const details = err.body.details;
    if (err.code === 'VALIDATION_FAILED' && details && typeof details === 'object') {
      const parts = Object.entries(details).flatMap(([field, msgs]) => (Array.isArray(msgs) ? msgs.map((m) => `${field}: ${m}`) : []));
      if (parts.length) return parts.join(' · ');
    }
    if (err.status === 429) return 'Zu viele Anfragen – bitte kurz warten.';
    return err.message || err.code;
  }
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler';
}

export function displayName(u: { displayName: string | null; username: string } | null | undefined): string {
  if (!u) return 'Unbekannt';
  return u.displayName || u.username;
}

/** Text in die Zwischenablage (ohne Fehler, wenn die API fehlt). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Relative API-Pfade (`/api/...`) zu absoluten URLs machen (für Anzeige/Kopieren). */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`;
}
